import React, { useContext, useEffect, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { Button, Image, Input, Overlay } from '@rneui/themed';
import { useForm, Controller } from 'react-hook-form';
import { post } from '../../../common/Api';
import Loading from '../Loading';
import Utils from '../../../common/Utils';
import AppContext from '../../../contexts/App';
import LayoutContext from '../../../contexts/Layout';

const logo = require('../../../../assets/images/splash.png');

const SUPPORTED_METHODS = Object.freeze(['EMAIL', 'TEXT']);

const PasswordReset = ({ setVisible, visible }) => {
    const { state } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const [step, setStep] = useState(1);
    const [resetMethod, setResetMethod] = useState(SUPPORTED_METHODS[0]);

    const {
        clearErrors,
        control,
        formState: { isSubmitting, isValid },
        getValues,
        handleSubmit,
        reset,
        setError
    } = useForm({
        mode: 'onBlur'
    });

    const handleResetPassword = async () => {
        if (resetMethod.equals(SUPPORTED_METHODS[0])) {
            const email = getValues('email');
            const domain = state.settings.websiteSettings?.domain;

            if (!email || !domain) {
                console.log('NEED TO ALERT USER OF BAD DATA');
            }

            const response = await post(`${state.settings.openIdSettings.authority}/Account/SendResetPasswordEmail`, { email, domain });

            if (response?.success) {
                setStep(2);
            } else {
                console.log(`Error processing request: ${response.errorMessage}`);
            }
        } else if (resetMethod.equals(SUPPORTED_METHODS[1])) {
            const phone = getValues('mobileNumber');

            if (!phone) {
                console.log('NEED TO ALERT USER OF BAD DATA');
            }

            const response = await post(`${state.settings.openIdSettings.authority}/Account/SendResetPasswordText`, { phone });

            if (response?.success) {
                setStep(2);
            } else {
                console.log(`Error processing request: ${response.errorMessage}`);
            }
        }
    };

    const handleConfirmation = async () => {
        const confirmationCode = getValues('confirmCode');

        if (confirmationCode && state.emailConfirmationId) {
            const endpoint = `${state.settings.openIdSettings.authority}/Account/VerifyConfirmationCode`;
            const response = await post(endpoint, { confirmationCodeId: state.emailConfirmationId, confirmationCode });

            if (!response?.success) {
                setError('confirmCode', {
                    type: 'custom',
                    message: 'Code does not match our records, please make sure you entered it correctly.'
                });
            } else {
                setVisible(false);
            }
        }
    };

    useEffect(() => {
        return () => {
            clearErrors(['email', 'mobileNumber', 'confirmCode']);
            reset();
        };
    }, [clearErrors, reset]);

    return (
        <Overlay
            backdropStyle={{ opacity: 0.5 }}
            isVisible={visible}
            onBackdropPress={() => setVisible(false)}
            overlayStyle={Styles.overlay}
            testID='Authentication.PasswordReset.Overlay'
        >
            <View style={{ flex: 1, padding: 10 }}>
                <Image
                    childrenContainerStyle={{ height: undefined, width: undefined }}
                    containerStyle={{
                        alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        marginBottom: layout.styleGuide.componentSeperator
                    }}
                    source={logo}
                    style={{
                        height: 160,
                        position: undefined,
                        resizeMode: 'contain',
                        tintColor: '#153d6e',
                        width: 200
                    }}
                />
                <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between', marginHorizontal: 10 }}>
                    <Button
                        buttonStyle={{
                            backgroundColor: resetMethod.equals(SUPPORTED_METHODS[0]) ? '#153d6e' : '#f7f8f9',
                            borderColor: '#153d6e',
                            borderWidth: 1
                        }}
                        onPress={() => setResetMethod(SUPPORTED_METHODS[0])}
                        title='RESET VIA EMAIL'
                        titleStyle={{
                            ...layout.styleGuide.h5,
                            color: resetMethod.equals(SUPPORTED_METHODS[0]) ? '#fff' : '#153d6e',
                            padding: 5
                        }}
                        type='solid'
                    />
                    <Button
                        buttonStyle={{
                            backgroundColor: resetMethod.equals(SUPPORTED_METHODS[1]) ? '#153d6e' : '#f7f8f9',
                            borderColor: '#153d6e',
                            borderWidth: 1
                        }}
                        onPress={() => setResetMethod(SUPPORTED_METHODS[1])}
                        title='RESET VIA TEXT'
                        titleStyle={{
                            ...layout.styleGuide.h5,
                            color: resetMethod.equals(SUPPORTED_METHODS[1]) ? '#fff' : '#153d6e',
                            padding: 5
                        }}
                        type='solid'
                    />
                </View>
                {!step ||
                    (step === 1 && (
                        <>
                            <View style={{ flex: 1 }}>
                                {
                                    /* Reset Via Email */
                                    resetMethod.equals(SUPPORTED_METHODS[0]) && (
                                        <Controller
                                            control={control}
                                            defaultValue=''
                                            name='email'
                                            render={({ field, fieldState }) => {
                                                return (
                                                    <Input
                                                        {...field}
                                                        autoCorrect={false}
                                                        autoFocus={false}
                                                        errorMessage={!fieldState.error ? 'EMAIL' : fieldState.error?.message}
                                                        errorStyle={!fieldState.error ? Styles.placeholderText : Styles.errorText}
                                                        innerRef={field.ref}
                                                        inputStyle={{ outlineStyle: 'none' }}
                                                        textContentType='emailAddress'
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required: {
                                                    message: 'Email address is required',
                                                    value: true
                                                },
                                                pattern: {
                                                    message: 'Invalid email address',
                                                    value: Utils.ValidEmailRegExp
                                                }
                                            }}
                                        />
                                    )
                                }
                                {
                                    /* Reset Via Text */
                                    resetMethod.equals(SUPPORTED_METHODS[1]) && (
                                        <Controller
                                            control={control}
                                            defaultValue=''
                                            name='mobileNumber'
                                            render={({ field, fieldState }) => {
                                                return (
                                                    <Input
                                                        {...field}
                                                        errorMessage={!fieldState.error ? 'MOBILE PHONE' : fieldState.error?.message}
                                                        errorStyle={!fieldState.error ? Styles.placeholderText : Styles.errorText}
                                                        innerRef={field.ref}
                                                        inputStyle={Styles.smallInput}
                                                        onChangeText={val => field.onChange(val)}
                                                        textContentType='telephoneNumber'
                                                        spellCheck={false}
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required: {
                                                    message: 'Mobile phone is required',
                                                    value: true
                                                },
                                                pattern: {
                                                    message: 'Invalid phone number',
                                                    value: Utils.ValidPhoneNumberRegExp
                                                }
                                            }}
                                        />
                                    )
                                }
                            </View>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginHorizontal: 10, marginTop: 20 }}>
                                <Button
                                    buttonStyle={{
                                        ...Styles.nextButton,
                                        backgroundColor: isValid ? '#153d6e' : '#f7f8f9'
                                    }}
                                    containerStyle={{ marginVertical: 20, width: '40%' }}
                                    onPress={() => handleSubmit(handleResetPassword)}
                                    size='md'
                                    title='NEXT'
                                    titleStyle={{
                                        ...layout.styleGuide.h5,
                                        color: isValid ? '#fff' : '#153d6e',
                                        padding: 5
                                    }}
                                    type='solid'
                                />
                                <Button
                                    buttonStyle={{
                                        ...Styles.cancelButton,
                                        backgroundColor: '#a61a2b'
                                    }}
                                    containerStyle={{ marginVertical: 20, width: '40%' }}
                                    onPress={() => setVisible(false)}
                                    size='md'
                                    title='CANCEL'
                                    titleStyle={{
                                        ...layout.styleGuide.h5,
                                        color: '#fff',
                                        padding: 5
                                    }}
                                    type='solid'
                                />
                            </View>
                        </>
                    ))}
                {step === 2 && (
                    <>
                        <View style={{ flex: 1 }}>
                            <Text style={Styles?.introductionText}>
                                If an account exists with this
                                {resetMethod.equals(SUPPORTED_METHODS[0]) ? ' email address you will receive an email ' : ' phone number you will receive an text '}
                                with a confirmation code.
                            </Text>
                            <Controller
                                control={control}
                                defaultValue=''
                                name='confirmCode'
                                render={({ field, fieldState }) => {
                                    return (
                                        <Input
                                            {...field}
                                            autoCorrect={false}
                                            autoFocus={false}
                                            errorMessage={!fieldState.error ? 'CONFIRMATION CODE' : fieldState.error?.message}
                                            errorStyle={!fieldState.error ? Styles.placeholderText : Styles.errorText}
                                            innerRef={field.ref}
                                            inputStyle={{ outlineStyle: 'none' }}
                                            textContentType='oneTimeCode'
                                        />
                                    );
                                }}
                                rules={{
                                    required: {
                                        message: 'Confirmation code is required',
                                        value: true
                                    }
                                }}
                            />
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginHorizontal: 10, marginTop: 20 }}>
                            <Button
                                buttonStyle={{
                                    ...Styles.nextButton,
                                    backgroundColor: isValid ? '#153d6e' : '#f7f8f9'
                                }}
                                containerStyle={{ marginVertical: 20, width: '40%' }}
                                onPress={() => handleSubmit(handleResetPassword)}
                                title='NEXT'
                                titleStyle={{
                                    ...layout.styleGuide.h5,
                                    color: isValid ? '#fff' : '#153d6e',
                                    padding: 5
                                }}
                                type='solid'
                            />
                            <Button
                                buttonStyle={{
                                    ...Styles.cancelButton,
                                    backgroundColor: '#a61a2b'
                                }}
                                containerStyle={{ marginVertical: 20, width: '40%' }}
                                onPress={() => setVisible(false)}
                                title='CANCEL'
                                titleStyle={{
                                    ...layout.styleGuide.h5,
                                    color: '#fff',
                                    padding: 5
                                }}
                                type='solid'
                            />
                        </View>
                    </>
                )}
                {isSubmitting && <Loading />}
            </View>
        </Overlay>
    );
};

const Styles = StyleSheet.create({
    alternateActionText: {
        color: '#a61a2b',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '12pt' : 12
    },
    cancelButton: {
        borderColor: '#fff',
        borderWidth: Platform.OS === 'web' ? '1pt' : 1,
        elevation: 10,
        fontFamily: 'GothamBook'
    },
    errorText: {
        color: '#a61a2b',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '14pt' : 14
    },
    introductionText: {
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '14pt' : 14,
        color: 'rgb(93, 110, 127)',
        padding: 20
    },
    nextButton: {
        borderColor: '#153d6e',
        borderWidth: Platform.OS === 'web' ? '1pt' : 1,
        elevation: 10
    },
    overlay: {
        minHeight: '40%',
        minWidth: '40%',
        paddingHorizontal: 20
    },
    placeholderText: {
        color: 'rgb(93, 110, 127)',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '12pt' : 12
    },
    smallInput: {
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '12pt' : 12
    }
});

export default PasswordReset;
